import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatTableDataSource } from '@angular/material/table';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import {
  formatPrepaidWashBookItemChip,
  PrepaidWashBook,
} from '../../../models/prepaid-wash-book.models';
import { PrepaidWashBookService } from '../../../services/prepaid-wash-book.service';
import { DeletePrepaidWashBookModalComponent } from '../../delete-prepaid-wash-book-modal/delete-prepaid-wash-book-modal.component';
import {
  PackageRowActionItem,
  PackageRowActionsComponent,
} from '../../../shared/components/package-row-actions/package-row-actions.component';

@Component({
  selector: 'app-prepaid-wash-books-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    DatePipe,
    CurrencyPipe,
    DeletePrepaidWashBookModalComponent,
    PackageRowActionsComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './prepaid-wash-books-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './prepaid-wash-books-table.component.scss',
})
export class PrepaidWashBooksTableComponent {
  private readonly prepaidWashBookService = inject(PrepaidWashBookService);
  private readonly messageService = inject(NotificationService);

  private _books: PrepaidWashBook[] = [];
  @Input() set books(value: PrepaidWashBook[]) {
    this._books = value;
    this.dataSource.data = value;
  }
  get books(): PrepaidWashBook[] {
    return this._books;
  }

  @Input() loading = false;
  @Output() deleted = new EventEmitter<void>();
  @Output() edit = new EventEmitter<PrepaidWashBook>();

  dataSource = new MatTableDataSource<PrepaidWashBook>();
  displayedColumns: string[] = [
    'bookCode',
    'packageName',
    'washConfigs',
    'status',
    'price',
    'orders',
    'redemptions',
    'lastUpdated',
    'actions',
  ];

  readonly rowActionItems: PackageRowActionItem[] = [
    {
      id: 'edit',
      label: 'Edit',
      icon: 'assets/images/package/edit.svg',
      iconSize: 11,
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: 'assets/images/package/trash.svg',
      danger: true,
      borderTop: true,
    },
  ];

  currentBook: PrepaidWashBook | null = null;
  bookToDelete: PrepaidWashBook | null = null;
  showDeleteModal = false;
  isDeleting = false;

  itemChip(item: PrepaidWashBook['items'][number]): string {
    return formatPrepaidWashBookItemChip(item);
  }

  washChipClass(type: string): string {
    switch (type) {
      case 'single_wash':
        return 'wash-type-single';
      case 'add_on':
        return 'wash-type-add-on';
      case 'monthly_membership':
        return 'wash-type-monthly';
      case 'detailing':
        return 'wash-type-detailing';
      default:
        return 'wash-type-single';
    }
  }

  toNumber(value: string | number): number {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }

  onRowAction(actionId: string): void {
    if (actionId === 'edit') {
      this.onEditBook();
      return;
    }
    if (actionId === 'delete') {
      this.onDeleteBook();
    }
  }

  onDeleteBook(): void {
    if (this.currentBook) {
      this.bookToDelete = this.currentBook;
      this.showDeleteModal = true;
    }
  }

  onEditBook(): void {
    if (this.currentBook) {
      this.edit.emit(this.currentBook);
    }
  }

  onCloseDeleteModal(): void {
    this.showDeleteModal = false;
    this.bookToDelete = null;
    this.isDeleting = false;
  }

  onConfirmDelete(book: PrepaidWashBook): void {
    if (!book?.id) {
      return;
    }
    this.isDeleting = true;
    this.prepaidWashBookService.deletePrepaidWashBook(book.id).subscribe({
      next: response => {
        this.isDeleting = false;
        if (response.status) {
          this.messageService.add({
            severity: 'success',
            summary: 'Prepaid wash book deleted',
            detail: `"${book.name}" has been removed successfully.`,
          });
          this.onCloseDeleteModal();
          this.deleted.emit();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.message || 'Failed to delete prepaid wash book.',
          });
          this.onCloseDeleteModal();
        }
      },
      error: error => {
        this.isDeleting = false;
        const errorMessage =
          error?.error?.message ||
          error?.message ||
          'Failed to delete prepaid wash book. Please try again.';
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage,
        });
        this.onCloseDeleteModal();
      },
    });
  }
}
